import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ScatterChart, Scatter, ZAxis } from 'recharts';
import { AlertTriangle, Shield, Network, Target, Calendar, TrendingUp, TrendingDown, Minus, Layers, Server, Database, Globe, Lock, Unlock, RefreshCw, Download, FileText, Filter, Search, X, ChevronDown, ChevronUp } from 'lucide-react';
// Mock data - in a real implementation, this would come from your API
const mockPredictedThreats = [{
  id: 'threat1',
  category: 'account_compromise',
  threatType: 'credential_theft',
  description: 'Credential theft through phishing or password reuse',
  likelihood: 0.85,
  impact: 0.75,
  priorityScore: 82,
  confidence: 0.78,
  predictedTimestamp: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
  affectedAssets: ['asset_authentication_service', 'asset_user_directory'],
  potentialAttackVectors: ['Phishing emails targeting employees', 'Password reuse from breached sites'],
  mitigationRecommendations: ['Implement multi-factor authentication for all user accounts', 'Conduct regular phishing awareness training', 'Deploy anti-phishing email filters'],
  evidenceFactors: ['Identity & access controls below recommended threshold (65%)', '3 account compromise attempts detected recently', 'High vulnerability exposure (72%)']
}, {
  id: 'threat2',
  category: 'data_exfiltration',
  threatType: 'data_exfiltration',
  description: 'Unauthorized data exfiltration through API abuse',
  likelihood: 0.72,
  impact: 0.88,
  priorityScore: 78,
  confidence: 0.82,
  predictedTimestamp: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
  affectedAssets: ['asset_customer_database', 'asset_api_gateway'],
  potentialAttackVectors: ['Abuse of legitimate API access', 'Exfiltration via encrypted channels to avoid detection'],
  mitigationRecommendations: ['Implement data loss prevention controls', 'Set up alerts for unusual data access patterns', 'Apply rate limiting on API endpoints'],
  evidenceFactors: ['Data protection controls below recommended threshold (58%)', 'High data sensitivity score (85%)', 'Significant external attack surface exposure (76%)']
}, {
  id: 'threat3',
  category: 'privilege_escalation',
  threatType: 'privilege_escalation',
  description: 'Privilege escalation through misconfigured permissions',
  likelihood: 0.65,
  impact: 0.82,
  priorityScore: 71,
  confidence: 0.75,
  predictedTimestamp: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
  affectedAssets: ['asset_admin_portal', 'asset_iam_system'],
  potentialAttackVectors: ['Exploitation of misconfigured role permissions', 'Session hijacking of administrative users'],
  mitigationRecommendations: ['Review and enforce least privilege principles', 'Implement just-in-time access for administrative functions', 'Audit permission changes regularly'],
  evidenceFactors: ['2 privilege escalation incidents in recent history', 'High vulnerability exposure (68%)', 'Elevated threat landscape score (73%)']
}, {
  id: 'threat4',
  category: 'malicious_ip',
  threatType: 'malicious_ip_access',
  description: 'Access attempts from known malicious IP addresses',
  likelihood: 0.78,
  impact: 0.62,
  priorityScore: 68,
  confidence: 0.88,
  predictedTimestamp: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
  affectedAssets: ['asset_web_application', 'asset_api_gateway'],
  potentialAttackVectors: ['Known malicious IP addresses attempting to access systems', 'Tor exit nodes used to mask origin'],
  mitigationRecommendations: ['Implement IP reputation filtering', 'Block access from high-risk countries', 'Deploy web application firewall'],
  evidenceFactors: ['Elevated threat landscape score (82%)', '5 malicious IP detections in recent history', 'High vulnerability exposure (65%)']
}, {
  id: 'threat5',
  category: 'suspicious_automation',
  threatType: 'automated_attack',
  description: 'Scripted API abuse to extract data or create accounts',
  likelihood: 0.7,
  impact: 0.68,
  priorityScore: 65,
  confidence: 0.72,
  predictedTimestamp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
  affectedAssets: ['asset_api_gateway', 'asset_web_application'],
  potentialAttackVectors: ['Scripted API abuse to extract data or create accounts', 'Credential stuffing using automated tools'],
  mitigationRecommendations: ['Implement rate limiting and throttling', 'Deploy bot detection mechanisms', 'Use CAPTCHA for sensitive operations'],
  evidenceFactors: ['12 security threats detected in last 30 days', 'API protection controls below threshold (62%)', 'Elevated threat landscape score (68%)']
}];
const mockAttackSurfaceMap = {
  assets: [{
    id: 'asset_web_application',
    name: 'Main Web Application',
    type: 'web_application',
    exposureLevel: 1.0,
    businessValue: 0.9,
    containsSensitiveData: true,
    vulnerabilityCount: 3
  }, {
    id: 'asset_api_gateway',
    name: 'API Gateway',
    type: 'api_gateway',
    exposureLevel: 0.9,
    businessValue: 0.85,
    containsSensitiveData: false,
    vulnerabilityCount: 2
  }, {
    id: 'asset_authentication_service',
    name: 'Authentication Service',
    type: 'auth_service',
    exposureLevel: 0.7,
    businessValue: 0.95,
    containsSensitiveData: true,
    vulnerabilityCount: 1
  }, {
    id: 'asset_user_directory',
    name: 'User Directory',
    type: 'directory_service',
    exposureLevel: 0.3,
    businessValue: 0.9,
    containsSensitiveData: true,
    vulnerabilityCount: 0
  }, {
    id: 'asset_customer_database',
    name: 'Customer Database',
    type: 'database',
    exposureLevel: 0.1,
    businessValue: 1.0,
    containsSensitiveData: true,
    vulnerabilityCount: 2
  }],
  entryPoints: ['asset_web_application', 'asset_api_gateway'],
  criticalAssets: ['asset_authentication_service', 'asset_customer_database', 'asset_user_directory'],
  vulnerabilitySummary: {
    critical: 2,
    high: 3,
    medium: 5,
    low: 8
  },
  exposureSummary: {
    internet: 2,
    thirdParty: 1,
    internal: 2
  }
};
const mockAttackChains = [{
  id: 'chain1',
  name: 'Web App to Customer Database',
  description: 'Attack chain from web application to customer database',
  threatCategory: 'data_exfiltration',
  impactScore: 85,
  likelihood: 0.65,
  affectedAssets: ['asset_web_application', 'asset_api_gateway', 'asset_customer_database'],
  attackPaths: [{
    nodes: [{
      assetId: 'asset_web_application',
      assetName: 'Main Web Application',
      step: 0
    }, {
      assetId: 'asset_api_gateway',
      assetName: 'API Gateway',
      step: 1
    }, {
      assetId: 'asset_customer_database',
      assetName: 'Customer Database',
      step: 2
    }],
    edges: [{
      sourceStep: 0,
      targetStep: 1,
      technique: 'API Abuse',
      successProbability: 0.8
    }, {
      sourceStep: 1,
      targetStep: 2,
      technique: 'Data Access',
      successProbability: 0.7
    }],
    exploitability: 0.65,
    complexity: 'medium'
  }]
}, {
  id: 'chain2',
  name: 'Authentication Service Compromise',
  description: 'Attack chain targeting authentication service',
  threatCategory: 'account_compromise',
  impactScore: 90,
  likelihood: 0.55,
  affectedAssets: ['asset_web_application', 'asset_authentication_service', 'asset_user_directory'],
  attackPaths: [{
    nodes: [{
      assetId: 'asset_web_application',
      assetName: 'Main Web Application',
      step: 0
    }, {
      assetId: 'asset_authentication_service',
      assetName: 'Authentication Service',
      step: 1
    }, {
      assetId: 'asset_user_directory',
      assetName: 'User Directory',
      step: 2
    }],
    edges: [{
      sourceStep: 0,
      targetStep: 1,
      technique: 'Authentication Bypass',
      successProbability: 0.6
    }, {
      sourceStep: 1,
      targetStep: 2,
      technique: 'Credential Access',
      successProbability: 0.5
    }],
    exploitability: 0.55,
    complexity: 'high'
  }]
}];
const mockCriticalAssets = [{
  assetId: 'asset_customer_database',
  assetName: 'Customer Database',
  assetType: 'database',
  criticalityScore: 95,
  attackPathCount: 3,
  maxImpactScore: 85,
  vulnerabilityCount: 2,
  isEntryPoint: false,
  isDataRepository: true
}, {
  assetId: 'asset_authentication_service',
  assetName: 'Authentication Service',
  assetType: 'auth_service',
  criticalityScore: 90,
  attackPathCount: 2,
  maxImpactScore: 90,
  vulnerabilityCount: 1,
  isEntryPoint: false,
  isDataRepository: true
}, {
  assetId: 'asset_web_application',
  assetName: 'Main Web Application',
  assetType: 'web_application',
  criticalityScore: 85,
  attackPathCount: 5,
  maxImpactScore: 75,
  vulnerabilityCount: 3,
  isEntryPoint: true,
  isDataRepository: false
}];
// Threat timeline data
const mockTimelineData = [{
  name: '1-7 Days',
  threats: 3,
  critical: 1,
  high: 2
}, {
  name: '8-14 Days',
  threats: 5,
  critical: 2,
  high: 3
}, {
  name: '15-30 Days',
  threats: 8,
  critical: 1,
  high: 4
}, {
  name: '31-90 Days',
  threats: 12,
  critical: 3,
  high: 5
}];
// Asset risk exposure data
const mockAssetRiskData = mockAttackSurfaceMap.assets.map(asset => ({
  name: asset.name,
  exposureLevel: asset.exposureLevel * 100,
  businessValue: asset.businessValue * 100,
  vulnerabilityCount: asset.vulnerabilityCount || 0,
  containsSensitiveData: asset.containsSensitiveData,
  z: 1
}));
// Threat categories
const threatCategories = {
  account_compromise: {
    label: 'Account Compromise',
    color: '#FF6B6B'
  },
  brute_force: {
    label: 'Brute Force',
    color: '#FF9E40'
  },
  data_exfiltration: {
    label: 'Data Exfiltration',
    color: '#4ECDC4'
  },
  privilege_escalation: {
    label: 'Privilege Escalation',
    color: '#A78BFA'
  },
  impossible_travel: {
    label: 'Impossible Travel',
    color: '#F8A102'
  },
  malicious_ip: {
    label: 'Malicious IP',
    color: '#FF5252'
  },
  suspicious_automation: {
    label: 'Suspicious Automation',
    color: '#45B7D1'
  },
  system_tampering: {
    label: 'System Tampering',
    color: '#FF85EA'
  },
  unusual_behavior: {
    label: 'Unusual Behavior',
    color: '#FFC857'
  }
};
// Asset type icons
const assetTypeIcons = {
  web_application: <Globe size={16} data-id="element-5629" />,
  api_gateway: <Server size={16} data-id="element-5630" />,
  auth_service: <Lock size={16} data-id="element-5631" />,
  directory_service: <Layers size={16} data-id="element-5632" />,
  database: <Database size={16} data-id="element-5633" />
};
const PredictiveThreatDashboard = () => {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState('month');
  const [loading, setLoading] = useState(false);
  const [predictedThreats, setPredictedThreats] = useState(mockPredictedThreats);
  const [attackSurfaceMap, setAttackSurfaceMap] = useState(mockAttackSurfaceMap);
  const [attackChains, setAttackChains] = useState(mockAttackChains);
  const [criticalAssets, setCriticalAssets] = useState(mockCriticalAssets);
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [selectedChain, setSelectedChain] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // Fetch data based on timeframe
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // In a real implementation, this would be API calls
        // const threatsResponse = await fetch(`/api/security/predictive/threats?timeframe=${timeframe}`);
        // const threats = await threatsResponse.json();
        // setPredictedThreats(threats);
        // Using mock data for now
        setTimeout(() => {
          setPredictedThreats(mockPredictedThreats);
          setAttackSurfaceMap(mockAttackSurfaceMap);
          setAttackChains(mockAttackChains);
          setCriticalAssets(mockCriticalAssets);
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error('Error fetching predictive threat data:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [timeframe]);
  // Filter threats based on category and search query
  const filteredThreats = useMemo(() => {
    return predictedThreats.filter(threat => {
      const matchesCategory = !filterCategory || threat.category === filterCategory;
      const matchesSearch = !searchQuery || threat.description.toLowerCase().includes(searchQuery.toLowerCase()) || threat.threatType.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [predictedThreats, filterCategory, searchQuery]);
  // Handle refresh
  const handleRefresh = () => {
    setLoading(true);
    // In a real implementation, this would trigger a new prediction
    // fetch('/api/security/predictive/analyze', { method: 'POST' })
    //   .then(() => fetchData())
    //   .catch(error => console.error('Error triggering prediction:', error));
    // Using mock data for now
    setTimeout(() => {
      setPredictedThreats(mockPredictedThreats);
      setAttackSurfaceMap(mockAttackSurfaceMap);
      setAttackChains(mockAttackChains);
      setCriticalAssets(mockCriticalAssets);
      setLoading(false);
    }, 1000);
  };
  // Handle report generation
  const handleGenerateReport = reportType => {
    // In a real implementation, this would generate a report
    // window.open(`/api/security/predictive/report/${reportType}`, '_blank');
    alert(`Generating ${reportType} report...`);
  };
  // Format date for display
  const formatDate = date => {
    return new Date(date).toLocaleDateString();
  };
  // Calculate days until predicted event
  const getDaysUntil = date => {
    const now = new Date();
    const predictedDate = new Date(date);
    const diffTime = Math.abs(predictedDate - now);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  // Get threat category information
  const getThreatCategory = category => {
    return threatCategories[category] || {
      label: category,
      color: '#999999'
    };
  };
  // Get asset type icon
  const getAssetTypeIcon = type => {
    return assetTypeIcons[type] || <Server size={16} data-id="element-5634" />;
  };
  // Threat card component
  const ThreatCard = ({
    threat,
    onClick,
    isSelected
  }) => <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 border-l-4 cursor-pointer transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-50' : `border-${getThreatCategory(threat.category).color}`}`} style={{
    borderLeftColor: getThreatCategory(threat.category).color
  }} onClick={() => onClick(threat)} data-id="element-5635">
      <div className="flex justify-between items-start" data-id="element-5636">
        <div data-id="element-5637">
          <div className="flex items-center mb-2" data-id="element-5638">
            <span className="inline-block w-3 h-3 rounded-full mr-2" style={{
            backgroundColor: getThreatCategory(threat.category).color
          }} data-id="element-5639"></span>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-5640">
              {getThreatCategory(threat.category).label}
            </h3>
          </div>
          <p className="text-base font-semibold text-gray-900 dark:text-white mb-2" data-id="element-5641">
            {threat.description}
          </p>
          <div className="flex flex-wrap gap-2 mt-2" data-id="element-5642">
            {threat.affectedAssets.slice(0, 2).map((assetId, index) => {
            const asset = attackSurfaceMap.assets.find(a => a.id === assetId);
            return asset ? <div key={index} className="flex items-center text-xs text-gray-600 dark:text-gray-400" data-id="element-5643">
                  {getAssetTypeIcon(asset.type)}
                  <span className="ml-1" data-id="element-5644">{asset.name}</span>
                </div> : null;
          })}
            {threat.affectedAssets.length > 2 && <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-5645">
                +{threat.affectedAssets.length - 2} more
              </div>}
          </div>
        </div>
        <div className="flex flex-col items-end" data-id="element-5646">
          <div className="flex items-center" data-id="element-5647">
            <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" data-id="element-5648" />
            <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-5649">
              {getDaysUntil(threat.predictedTimestamp)} days
            </span>
          </div>
          <div className={`text-sm font-bold mt-2 px-2 py-1 rounded ${threat.priorityScore >= 80 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : threat.priorityScore >= 60 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`} data-id="element-5650">
            {threat.priorityScore}
          </div>
        </div>
      </div>
    </div>;
  // Attack chain card component
  const AttackChainCard = ({
    chain,
    onClick,
    isSelected
  }) => <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 cursor-pointer transition-all ${isSelected ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`} onClick={() => onClick(chain)} data-id="element-5651">
      <div className="flex justify-between items-start" data-id="element-5652">
        <div data-id="element-5653">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1" data-id="element-5654">
            {chain.name}
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2" data-id="element-5655">
            {chain.description}
          </p>
          <div className="flex items-center text-xs" data-id="element-5656">
            <span className="inline-block w-2 h-2 rounded-full mr-1" style={{
            backgroundColor: getThreatCategory(chain.threatCategory).color
          }} data-id="element-5657"></span>
            <span className="text-gray-600 dark:text-gray-400" data-id="element-5658">
              {getThreatCategory(chain.threatCategory).label}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end" data-id="element-5659">
          <div className={`text-sm font-bold px-2 py-1 rounded ${chain.impactScore >= 80 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : chain.impactScore >= 60 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`} data-id="element-5660">
            {chain.impactScore}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1" data-id="element-5661">
            {Math.round(chain.likelihood * 100)}% likelihood
          </div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700" data-id="element-5662">
        <div className="flex items-center text-xs text-gray-600 dark:text-gray-400" data-id="element-5663">
          <Network className="h-3 w-3 mr-1" data-id="element-5664" />
          <span data-id="element-5665">
            {chain.attackPaths.length} attack path
            {chain.attackPaths.length !== 1 ? 's' : ''}
          </span>
          <span className="mx-2" data-id="element-5666">•</span>
          <span data-id="element-5667">
            {chain.affectedAssets.length} affected asset
            {chain.affectedAssets.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>;
  // Critical asset card component
  const CriticalAssetCard = ({
    asset
  }) => <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4" data-id="element-5668">
      <div className="flex justify-between items-start" data-id="element-5669">
        <div data-id="element-5670">
          <div className="flex items-center mb-1" data-id="element-5671">
            {getAssetTypeIcon(asset.assetType)}
            <h3 className="text-sm font-medium text-gray-900 dark:text-white ml-1" data-id="element-5672">
              {asset.assetName}
            </h3>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-2" data-id="element-5673">
            {asset.assetType}
          </div>
        </div>
        <div className={`text-sm font-bold px-2 py-1 rounded ${asset.criticalityScore >= 90 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : asset.criticalityScore >= 70 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`} data-id="element-5674">
          {asset.criticalityScore}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700" data-id="element-5675">
        <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-5676">
          <div className="flex items-center text-gray-600 dark:text-gray-400" data-id="element-5677">
            <Network className="h-3 w-3 mr-1" data-id="element-5678" />
            <span data-id="element-5679">
              {asset.attackPathCount} path
              {asset.attackPathCount !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-400" data-id="element-5680">
            <AlertTriangle className="h-3 w-3 mr-1" data-id="element-5681" />
            <span data-id="element-5682">
              {asset.vulnerabilityCount} vuln
              {asset.vulnerabilityCount !== 1 ? 's' : ''}
            </span>
          </div>
          {asset.isEntryPoint && <div className="flex items-center text-orange-600 dark:text-orange-400" data-id="element-5683">
              <Unlock className="h-3 w-3 mr-1" data-id="element-5684" />
              <span data-id="element-5685">Entry point</span>
            </div>}
          {asset.isDataRepository && <div className="flex items-center text-purple-600 dark:text-purple-400" data-id="element-5686">
              <Database className="h-3 w-3 mr-1" data-id="element-5687" />
              <span data-id="element-5688">Sensitive data</span>
            </div>}
        </div>
      </div>
    </div>;
  // Threat detail panel
  const ThreatDetailPanel = ({
    threat
  }) => {
    if (!threat) return null;
    return <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5689">
        <div className="flex justify-between items-start mb-4" data-id="element-5690">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5691">
            Threat Details
          </h2>
          <button onClick={() => setSelectedThreat(null)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-5692">
            <X size={20} data-id="element-5693" />
          </button>
        </div>
        <div className="mb-4" data-id="element-5694">
          <div className="flex items-center mb-2" data-id="element-5695">
            <span className="inline-block w-3 h-3 rounded-full mr-2" style={{
            backgroundColor: getThreatCategory(threat.category).color
          }} data-id="element-5696"></span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5697">
              {getThreatCategory(threat.category).label}
            </span>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-5698">
            {threat.description}
          </h3>
          <div className="flex flex-wrap gap-4 text-sm mb-4" data-id="element-5699">
            <div className="flex items-center" data-id="element-5700">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5701">
                Type:
              </div>
              <div className="text-gray-600 dark:text-gray-400" data-id="element-5702">
                {threat.threatType.replace(/_/g, ' ')}
              </div>
            </div>
            <div className="flex items-center" data-id="element-5703">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5704">
                Predicted:
              </div>
              <div className="text-gray-600 dark:text-gray-400" data-id="element-5705">
                {formatDate(threat.predictedTimestamp)}
              </div>
            </div>
            <div className="flex items-center" data-id="element-5706">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5707">
                Confidence:
              </div>
              <div className="text-gray-600 dark:text-gray-400" data-id="element-5708">
                {Math.round(threat.confidence * 100)}%
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-5709">
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-5710">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5711">
              Priority Score
            </div>
            <div className="flex items-center" data-id="element-5712">
              <div className={`text-2xl font-bold ${threat.priorityScore >= 80 ? 'text-red-600 dark:text-red-400' : threat.priorityScore >= 60 ? 'text-orange-600 dark:text-orange-400' : 'text-yellow-600 dark:text-yellow-400'}`} data-id="element-5713">
                {threat.priorityScore}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 ml-2" data-id="element-5714">
                /100
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-5715">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5716">
              Likelihood
            </div>
            <div className="flex items-center" data-id="element-5717">
              <div className={`text-2xl font-bold ${threat.likelihood >= 0.8 ? 'text-red-600 dark:text-red-400' : threat.likelihood >= 0.6 ? 'text-orange-600 dark:text-orange-400' : 'text-yellow-600 dark:text-yellow-400'}`} data-id="element-5718">
                {Math.round(threat.likelihood * 100)}%
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-5719">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5720">
              Impact
            </div>
            <div className="flex items-center" data-id="element-5721">
              <div className={`text-2xl font-bold ${threat.impact >= 0.8 ? 'text-red-600 dark:text-red-400' : threat.impact >= 0.6 ? 'text-orange-600 dark:text-orange-400' : 'text-yellow-600 dark:text-yellow-400'}`} data-id="element-5722">
                {Math.round(threat.impact * 100)}%
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-5723">
          <div data-id="element-5724">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5725">
              Affected Assets
            </h4>
            <div className="space-y-2" data-id="element-5726">
              {threat.affectedAssets.map((assetId, index) => {
              const asset = attackSurfaceMap.assets.find(a => a.id === assetId);
              return asset ? <div key={index} className="flex items-center bg-gray-50 dark:bg-gray-700 p-2 rounded" data-id="element-5727">
                    {getAssetTypeIcon(asset.type)}
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-5728">
                      {asset.name}
                    </span>
                    {attackSurfaceMap.criticalAssets.includes(assetId) && <span className="ml-auto text-xs px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full" data-id="element-5729">
                        Critical
                      </span>}
                  </div> : null;
            })}
            </div>
          </div>
          <div data-id="element-5730">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5731">
              Potential Attack Vectors
            </h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-5732">
              {threat.potentialAttackVectors.map((vector, index) => <li key={index} data-id="element-5733">{vector}</li>)}
            </ul>
          </div>
        </div>
        <div className="mb-6" data-id="element-5734">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5735">
            Evidence Factors
          </h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-5736">
            {threat.evidenceFactors.map((factor, index) => <li key={index} data-id="element-5737">{factor}</li>)}
          </ul>
        </div>
        <div data-id="element-5738">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5739">
            Mitigation Recommendations
          </h4>
          <div className="space-y-2" data-id="element-5740">
            {threat.mitigationRecommendations.map((recommendation, index) => <div key={index} className="flex items-start bg-blue-50 dark:bg-blue-900 p-3 rounded" data-id="element-5741">
                <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5" data-id="element-5742" />
                <span className="text-sm text-blue-800 dark:text-blue-200" data-id="element-5743">
                  {recommendation}
                </span>
              </div>)}
          </div>
        </div>
      </div>;
  };
  // Attack chain detail panel
  const AttackChainDetailPanel = ({
    chain
  }) => {
    if (!chain) return null;
    return <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5744">
        <div className="flex justify-between items-start mb-4" data-id="element-5745">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5746">
            Attack Chain Details
          </h2>
          <button onClick={() => setSelectedChain(null)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-5747">
            <X size={20} data-id="element-5748" />
          </button>
        </div>
        <div className="mb-4" data-id="element-5749">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-5750">
            {chain.name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-5751">
            {chain.description}
          </p>
          <div className="flex flex-wrap gap-4 text-sm" data-id="element-5752">
            <div className="flex items-center" data-id="element-5753">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5754">
                Category:
              </div>
              <div className="flex items-center" data-id="element-5755">
                <span className="inline-block w-2 h-2 rounded-full mr-1" style={{
                backgroundColor: getThreatCategory(chain.threatCategory).color
              }} data-id="element-5756"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5757">
                  {getThreatCategory(chain.threatCategory).label}
                </span>
              </div>
            </div>
            <div className="flex items-center" data-id="element-5758">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5759">
                Impact:
              </div>
              <div className={`px-2 py-0.5 rounded ${chain.impactScore >= 80 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : chain.impactScore >= 60 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`} data-id="element-5760">
                {chain.impactScore}
              </div>
            </div>
            <div className="flex items-center" data-id="element-5761">
              <div className="font-medium text-gray-700 dark:text-gray-300 mr-1" data-id="element-5762">
                Likelihood:
              </div>
              <div className="text-gray-600 dark:text-gray-400" data-id="element-5763">
                {Math.round(chain.likelihood * 100)}%
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-5764">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5765">
            Attack Paths
          </h4>
          {chain.attackPaths.map((path, pathIndex) => <div key={pathIndex} className="mb-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-5766">
              <div className="flex justify-between items-center mb-3" data-id="element-5767">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5768">
                  Path {pathIndex + 1}
                </div>
                <div className="flex items-center text-xs" data-id="element-5769">
                  <div className="mr-3 text-gray-600 dark:text-gray-400" data-id="element-5770">
                    Exploitability: {Math.round(path.exploitability * 100)}%
                  </div>
                  <div className="text-gray-600 dark:text-gray-400" data-id="element-5771">
                    Complexity: {path.complexity}
                  </div>
                </div>
              </div>
              <div className="relative" data-id="element-5772">
                {/* Path nodes and edges visualization */}
                {path.nodes.map((node, nodeIndex) => <div key={nodeIndex} className="flex items-center mb-2" data-id="element-5773">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-800 dark:text-blue-200 font-medium" data-id="element-5774">
                      {nodeIndex + 1}
                    </div>
                    <div className="ml-3 flex-grow bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3" data-id="element-5775">
                      <div className="flex items-center" data-id="element-5776">
                        {getAssetTypeIcon(attackSurfaceMap.assets.find(a => a.id === node.assetId)?.type)}
                        <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5777">
                          {node.assetName}
                        </span>
                      </div>
                    </div>
                    {/* Edge/arrow to next node */}
                    {nodeIndex < path.nodes.length - 1 && <div className="absolute left-4 ml-0.5 w-0.5 bg-gray-300 dark:bg-gray-600" style={{
                top: `${nodeIndex * 48 + 32}px`,
                height: '32px'
              }} data-id="element-5778"></div>}
                    {/* Edge label */}
                    {nodeIndex < path.edges.length && <div className="absolute left-12 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded" style={{
                top: `${nodeIndex * 48 + 42}px`
              }} data-id="element-5779">
                        {path.edges[nodeIndex].technique} (
                        {Math.round(path.edges[nodeIndex].successProbability * 100)}
                        %)
                      </div>}
                  </div>)}
              </div>
            </div>)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5780">
          <div data-id="element-5781">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5782">
              Affected Assets
            </h4>
            <div className="space-y-2" data-id="element-5783">
              {chain.affectedAssets.map((assetId, index) => {
              const asset = attackSurfaceMap.assets.find(a => a.id === assetId);
              return asset ? <div key={index} className="flex items-center bg-gray-50 dark:bg-gray-700 p-2 rounded" data-id="element-5784">
                    {getAssetTypeIcon(asset.type)}
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-5785">
                      {asset.name}
                    </span>
                    {attackSurfaceMap.criticalAssets.includes(assetId) && <span className="ml-auto text-xs px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full" data-id="element-5786">
                        Critical
                      </span>}
                  </div> : null;
            })}
            </div>
          </div>
          <div data-id="element-5787">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3" data-id="element-5788">
              Mitigation Recommendations
            </h4>
            <div className="space-y-2" data-id="element-5789">
              {chain.mitigationRecommendations && chain.mitigationRecommendations.map((recommendation, index) => <div key={index} className="flex items-start bg-blue-50 dark:bg-blue-900 p-3 rounded" data-id="element-5790">
                    <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5" data-id="element-5791" />
                    <span className="text-sm text-blue-800 dark:text-blue-200" data-id="element-5792">
                      {recommendation}
                    </span>
                  </div>)}
              {(!chain.mitigationRecommendations || chain.mitigationRecommendations.length === 0) && <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-5793">
                  No specific recommendations available for this attack chain.
                </div>}
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900" data-id="element-5794">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5795">
        <div className="flex justify-between items-center mb-6" data-id="element-5796">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5797">
            Predictive Threat Modeling
          </h1>
          <div className="flex space-x-4" data-id="element-5798">
            <select className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-3 text-sm" value={timeframe} onChange={e => setTimeframe(e.target.value)} data-id="element-5799">
              <option value="week" data-id="element-5800">Next 7 Days</option>
              <option value="month" data-id="element-5801">Next 30 Days</option>
              <option value="quarter" data-id="element-5802">Next 90 Days</option>
            </select>
            <button onClick={handleRefresh} className="inline-flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700" disabled={loading} data-id="element-5803">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-5804" />
              Refresh
            </button>
            <div className="relative inline-block text-left" data-id="element-5805">
              <div data-id="element-5806">
                <button type="button" className="inline-flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700" id="report-menu" aria-expanded="true" aria-haspopup="true" onClick={() => {
                const menu = document.getElementById('report-menu-items');
                menu.classList.toggle('hidden');
              }} data-id="element-5807">
                  <FileText className="mr-2 h-4 w-4" data-id="element-5808" />
                  Reports
                  <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-5809">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-5810" />
                  </svg>
                </button>
              </div>
              <div className="hidden origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-10" role="menu" aria-orientation="vertical" aria-labelledby="report-menu" id="report-menu-items" data-id="element-5811">
                <div className="py-1" role="none" data-id="element-5812">
                  <button className="text-gray-700 dark:text-gray-200 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700" role="menuitem" onClick={() => handleGenerateReport('executive')} data-id="element-5813">
                    Executive Summary
                  </button>
                  <button className="text-gray-700 dark:text-gray-200 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700" role="menuitem" onClick={() => handleGenerateReport('technical')} data-id="element-5814">
                    Technical Report
                  </button>
                  <button className="text-gray-700 dark:text-gray-200 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700" role="menuitem" onClick={() => handleGenerateReport('attackSurface')} data-id="element-5815">
                    Attack Surface Map
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6" data-id="element-5816">
          {/* Predicted Threats Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5817">
            <div className="flex justify-between items-start" data-id="element-5818">
              <div data-id="element-5819">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5820">
                  Predicted Threats
                </p>
                <div className="mt-1" data-id="element-5821">
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white" data-id="element-5822">
                    {predictedThreats.length}
                  </p>
                </div>
              </div>
              <div className="rounded-full p-2 bg-red-100 dark:bg-red-900" data-id="element-5823">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" data-id="element-5824" />
              </div>
            </div>
            <div className="mt-4 flex space-x-4 text-sm" data-id="element-5825">
              <div className="flex items-center" data-id="element-5826">
                <span className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-5827"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5828">
                  {predictedThreats.filter(t => t.priorityScore >= 80).length}{' '}
                  Critical
                </span>
              </div>
              <div className="flex items-center" data-id="element-5829">
                <span className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-5830"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5831">
                  {predictedThreats.filter(t => t.priorityScore >= 60 && t.priorityScore < 80).length}{' '}
                  High
                </span>
              </div>
              <div className="flex items-center" data-id="element-5832">
                <span className="w-3 h-3 rounded-full bg-yellow-500 mr-1" data-id="element-5833"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5834">
                  {predictedThreats.filter(t => t.priorityScore < 60).length}{' '}
                  Medium
                </span>
              </div>
            </div>
          </div>
          {/* Attack Chains Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5835">
            <div className="flex justify-between items-start" data-id="element-5836">
              <div data-id="element-5837">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5838">
                  Attack Chains
                </p>
                <div className="mt-1" data-id="element-5839">
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white" data-id="element-5840">
                    {attackChains.length}
                  </p>
                </div>
              </div>
              <div className="rounded-full p-2 bg-purple-100 dark:bg-purple-900" data-id="element-5841">
                <Network className="h-6 w-6 text-purple-600 dark:text-purple-400" data-id="element-5842" />
              </div>
            </div>
            <div className="mt-4 flex space-x-4 text-sm" data-id="element-5843">
              <div className="flex items-center" data-id="element-5844">
                <span className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-5845"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5846">
                  {attackChains.filter(c => c.impactScore >= 80).length}{' '}
                  Critical
                </span>
              </div>
              <div className="flex items-center" data-id="element-5847">
                <span className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-5848"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5849">
                  {attackChains.filter(c => c.impactScore >= 60 && c.impactScore < 80).length}{' '}
                  High
                </span>
              </div>
            </div>
          </div>
          {/* Critical Assets Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5850">
            <div className="flex justify-between items-start" data-id="element-5851">
              <div data-id="element-5852">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5853">
                  Critical Assets
                </p>
                <div className="mt-1" data-id="element-5854">
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white" data-id="element-5855">
                    {criticalAssets.length}
                  </p>
                </div>
              </div>
              <div className="rounded-full p-2 bg-blue-100 dark:bg-blue-900" data-id="element-5856">
                <Target className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-5857" />
              </div>
            </div>
            <div className="mt-4 flex space-x-4 text-sm" data-id="element-5858">
              <div className="flex items-center" data-id="element-5859">
                <span className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-5860"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5861">
                  {criticalAssets.filter(a => a.criticalityScore >= 90).length}{' '}
                  High Risk
                </span>
              </div>
              <div className="flex items-center" data-id="element-5862">
                <span className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-5863"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5864">
                  {criticalAssets.filter(a => a.criticalityScore < 90).length}{' '}
                  Medium Risk
                </span>
              </div>
            </div>
          </div>
          {/* Vulnerabilities Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5865">
            <div className="flex justify-between items-start" data-id="element-5866">
              <div data-id="element-5867">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-5868">
                  Vulnerabilities
                </p>
                <div className="mt-1" data-id="element-5869">
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white" data-id="element-5870">
                    {attackSurfaceMap.vulnerabilitySummary.critical + attackSurfaceMap.vulnerabilitySummary.high + attackSurfaceMap.vulnerabilitySummary.medium + attackSurfaceMap.vulnerabilitySummary.low}
                  </p>
                </div>
              </div>
              <div className="rounded-full p-2 bg-orange-100 dark:bg-orange-900" data-id="element-5871">
                <Shield className="h-6 w-6 text-orange-600 dark:text-orange-400" data-id="element-5872" />
              </div>
            </div>
            <div className="mt-4 flex space-x-4 text-sm" data-id="element-5873">
              <div className="flex items-center" data-id="element-5874">
                <span className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-5875"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5876">
                  {attackSurfaceMap.vulnerabilitySummary.critical} Critical
                </span>
              </div>
              <div className="flex items-center" data-id="element-5877">
                <span className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-5878"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5879">
                  {attackSurfaceMap.vulnerabilitySummary.high} High
                </span>
              </div>
              <div className="flex items-center" data-id="element-5880">
                <span className="w-3 h-3 rounded-full bg-yellow-500 mr-1" data-id="element-5881"></span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-5882">
                  {attackSurfaceMap.vulnerabilitySummary.medium} Medium
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-5883">
          {/* Threat Timeline Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5884">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5885">
              Threat Timeline
            </h2>
            <div className="h-80" data-id="element-5886">
              <ResponsiveContainer width="100%" height="100%" data-id="element-5887">
                <BarChart data={mockTimelineData} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-5888">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-5889" />
                  <XAxis dataKey="name" data-id="element-5890" />
                  <YAxis data-id="element-5891" />
                  <Tooltip data-id="element-5892" />
                  <Legend data-id="element-5893" />
                  <Bar dataKey="threats" name="All Threats" fill="#8884d8" data-id="element-5894" />
                  <Bar dataKey="critical" name="Critical" fill="#FF3B30" data-id="element-5895" />
                  <Bar dataKey="high" name="High" fill="#FF9500" data-id="element-5896" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* Asset Risk Map Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-5897">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5898">
              Asset Risk Map
            </h2>
            <div className="h-80" data-id="element-5899">
              <ResponsiveContainer width="100%" height="100%" data-id="element-5900">
                <ScatterChart margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 10
              }} data-id="element-5901">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-5902" />
                  <XAxis type="number" dataKey="exposureLevel" name="Exposure Level" unit="%" domain={[0, 100]} label={{
                  value: 'Exposure Level',
                  position: 'bottom',
                  offset: 0
                }} data-id="element-5903" />
                  <YAxis type="number" dataKey="businessValue" name="Business Value" unit="%" domain={[0, 100]} label={{
                  value: 'Business Value',
                  angle: -90,
                  position: 'left'
                }} data-id="element-5904" />
                  <ZAxis type="number" dataKey="vulnerabilityCount" range={[40, 200]} name="Vulnerabilities" data-id="element-5905" />
                  <Tooltip cursor={{
                  strokeDasharray: '3 3'
                }} formatter={(value, name, props) => {
                  if (name === 'Exposure Level' || name === 'Business Value') {
                    return [`${value}%`, name];
                  }
                  return [value, name];
                }} content={({
                  active,
                  payload
                }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return <div className="bg-white dark:bg-gray-700 p-2 border border-gray-200 dark:border-gray-600 rounded shadow" data-id="element-5907">
                            <p className="font-medium" data-id="element-5908">{data.name}</p>
                            <p data-id="element-5909">Business Value: {data.businessValue}%</p>
                            <p data-id="element-5910">Exposure Level: {data.exposureLevel}%</p>
                            <p data-id="element-5911">Vulnerabilities: {data.vulnerabilityCount}</p>
                            <p data-id="element-5912">
                              Sensitive Data:{' '}
                              {data.containsSensitiveData ? 'Yes' : 'No'}
                            </p>
                          </div>;
                  }
                  return null;
                }} data-id="element-5906" />
                  <Scatter name="Assets" data={mockAssetRiskData} fill={entry => entry.containsSensitiveData ? '#FF3B30' : '#4285F4'} shape="circle" data-id="element-5913" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5914">
          {/* Predicted Threats Column */}
          <div className="lg:col-span-1" data-id="element-5915">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6" data-id="element-5916">
              <div className="flex justify-between items-center mb-4" data-id="element-5917">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-5918">
                  Predicted Threats
                </h2>
                <div className="flex space-x-2" data-id="element-5919">
                  <div className="relative" data-id="element-5920">
                    <button onClick={() => {
                    const menu = document.getElementById('filter-menu');
                    menu.classList.toggle('hidden');
                  }} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-5921">
                      <Filter className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-5922" />
                    </button>
                    <div id="filter-menu" className="hidden absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700" data-id="element-5923">
                      <div className="p-2" data-id="element-5924">
                        <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5925">
                          Threat Category
                        </div>
                        <div className="space-y-1" data-id="element-5926">
                          <button onClick={() => setFilterCategory('')} className={`w-full text-left px-2 py-1 text-sm rounded ${filterCategory === '' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} data-id="element-5927">
                            All Categories
                          </button>
                          {Object.entries(threatCategories).map(([key, {
                          label
                        }]) => <button key={key} onClick={() => setFilterCategory(key)} className={`w-full text-left px-2 py-1 text-sm rounded ${filterCategory === key ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} data-id="element-5928">
                                {label}
                              </button>)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="relative" data-id="element-5929">
                    <input type="text" placeholder="Search threats..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-5930" />
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" data-id="element-5931" />
                  </div>
                </div>
              </div>
              <div className="space-y-2 max-h-[calc(100vh-24rem)] overflow-y-auto pr-2" data-id="element-5932">
                {filteredThreats.map(threat => <ThreatCard key={threat.id} threat={threat} onClick={setSelectedThreat} isSelected={selectedThreat && selectedThreat.id === threat.id} data-id="element-5933" />)}
                {filteredThreats.length === 0 && <div className="text-center py-8 text-gray-500 dark:text-gray-400" data-id="element-5934">
                    No threats match your criteria
                  </div>}
              </div>
            </div>
          </div>
          {/* Details Column */}
          <div className="lg:col-span-2" data-id="element-5935">
            {selectedThreat ? <ThreatDetailPanel threat={selectedThreat} data-id="element-5936" /> : selectedChain ? <AttackChainDetailPanel chain={selectedChain} data-id="element-5937" /> : <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5938">
                {/* Attack Chains Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4" data-id="element-5939">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5940">
                    Attack Chains
                  </h2>
                  <div className="space-y-2 max-h-[calc(100vh-24rem)] overflow-y-auto pr-2" data-id="element-5941">
                    {attackChains.map(chain => <AttackChainCard key={chain.id} chain={chain} onClick={setSelectedChain} isSelected={false} data-id="element-5942" />)}
                  </div>
                </div>
                {/* Critical Assets Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4" data-id="element-5943">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5944">
                    Critical Assets
                  </h2>
                  <div className="space-y-2 max-h-[calc(100vh-24rem)] overflow-y-auto pr-2" data-id="element-5945">
                    {criticalAssets.map(asset => <CriticalAssetCard key={asset.assetId} asset={asset} data-id="element-5946" />)}
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default PredictiveThreatDashboard;